import { useEffect, useMemo, useState } from "react";
import { CreateButton, List } from "@refinedev/antd";
import { useList, useNavigation, useOne } from "@refinedev/core";
import { Button, Space, Table, Tag, Typography, message } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { AppImagePreview } from "../../../components";
import {
  TV_TARGET_LABELS,
  TvSettings,
  TvSlide,
  tvErrorMessage,
  tvLeagueTitle,
} from "../Lib";
import { reorderTvSlides } from "../Providers/tv-api";
import { TvDelete } from "../Components";

/** Цель слайда удалена: в базе пусто (ON DELETE SET NULL) — пишем это, а не «№null». */
const DELETED = "цель удалена";

const targetText = (s: TvSlide) => {
  const t = s.target;
  switch (t.type) {
    case "VIDEO":
      return t.videoId != null ? `видео №${t.videoId}` : DELETED;
    case "MATCH":
      return `матч №${t.matchId}, ${tvLeagueTitle(t.leagueId)}`;
    case "LEAGUE":
      return tvLeagueTitle(t.leagueId);
    case "SHOW":
      return t.showId != null ? `/show/${t.showSlug ?? t.showId}` : DELETED;
    case "COLLECTION":
      return t.collectionId != null
        ? `/collection/${t.collectionSlug ?? t.collectionId}`
        : DELETED;
    case "URL":
      return t.url ?? "";
  }
};

/** Что видит зритель сейчас и почему слайда нет на сайте. */
const stateTag = (s: TvSlide) => {
  const now = dayjs();
  if (s.shownNow) return <Tag color="green">На сайте</Tag>;
  if (!s.isActive) return <Tag color="orange">Выключен</Tag>;
  if (s.activeFrom && dayjs(s.activeFrom).isAfter(now))
    return <Tag>С {dayjs(s.activeFrom).format("DD.MM HH:mm")}</Tag>;
  if (s.activeTo && !dayjs(s.activeTo).isAfter(now))
    return <Tag color="default">Показ закончился</Tag>;
  return (
    <Tag title="Цель не опубликована, матч уже закончился или слайд не входит в число слайдов витрины">
      Не на сайте
    </Tag>
  );
};

export const TvShowcasePage = () => {
  const { push } = useNavigation();
  const [order, setOrder] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, refetch } = useList<TvSlide>({
    resource: "tv-showcase",
    dataProviderName: "tvDataProvider",
    pagination: { mode: "off" },
  });
  const { data: settings } = useOne<TvSettings>({
    resource: "tv-settings",
    dataProviderName: "tvDataProvider",
    id: "current",
  });
  const maxSlides = settings?.data?.showcaseMaxSlides;

  const slides = useMemo(
    () =>
      // все слайды — на главной: страницы лиг витрину не показывают, фильтр по лигам убран
      [...(data?.data ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
      ),
    [data]
  );

  useEffect(() => setOrder(slides.map((s) => s.id)), [slides]);

  const ordered = order
    .map((id) => slides.find((s) => s.id === id))
    .filter(Boolean) as TvSlide[];
  const changed = order.join(",") !== slides.map((s) => s.id).join(",");

  const move = (index: number, delta: number) => {
    const next = [...order];
    const [id] = next.splice(index, 1);
    next.splice(index + delta, 0, id);
    setOrder(next);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      await reorderTvSlides(order);
      message.success("Порядок слайдов сохранён");
      await refetch();
    } catch (e) {
      message.error(tvErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <List
      title="ФНЛ ТВ — витрина"
      resource="tv-showcase"
      breadcrumb={false}
      canCreate={false}
      headerButtons={
        <CreateButton resource="tv-showcase">Добавить слайд</CreateButton>
      }
    >
      <Typography.Paragraph type="secondary">
        На сайте показываются первые {maxSlides ?? 10} подходящих слайдов (число
        меняется в «Настройках»): включённые, в своём периоде, с опубликованной
        целью. Слайд матча пропадает после финального свистка, если это не
        отключено.
      </Typography.Paragraph>
      <Space style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          disabled={!changed}
          loading={saving}
          onClick={saveOrder}
        >
          Сохранить порядок
        </Button>
        {changed && (
          <Typography.Text type="warning">
            Порядок изменён, но не сохранён
          </Typography.Text>
        )}
      </Space>
      <Table<TvSlide>
        dataSource={ordered}
        loading={isLoading}
        rowKey="id"
        pagination={false}
        scroll={{ x: 1100 }}
        locale={{ emptyText: <div style={{ margin: 48 }}>Слайдов нет</div> }}
      >
        <Table.Column<TvSlide>
          title="№"
          width={110}
          render={(_, s, index) => (
            <Space>
              <Typography.Text strong>{index + 1}</Typography.Text>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === ordered.length - 1}
                onClick={() => move(index, 1)}
              />
            </Space>
          )}
        />
        <Table.Column<TvSlide>
          title="Обложка"
          width={160}
          render={(_, s) =>
            s.coverUrl ? (
              <AppImagePreview
                imageURL={s.coverUrl}
                useS3={!s.coverUrl.startsWith("http")}
              />
            ) : (
              <Typography.Text type="secondary">Без фото</Typography.Text>
            )
          }
        />
        <Table.Column<TvSlide>
          title="Слайд"
          render={(_, s) => (
            <Space direction="vertical" size={2}>
              <Typography.Text strong>{s.title}</Typography.Text>
              {s.subtitle && (
                <Typography.Text
                  type="secondary"
                  ellipsis={{ tooltip: s.subtitle }}
                  style={{ maxWidth: 420 }}
                >
                  {s.subtitle}
                </Typography.Text>
              )}
              <Typography.Text type="secondary">
                {TV_TARGET_LABELS[s.target.type]}: {targetText(s)}
              </Typography.Text>
              <Space size={4} wrap>
                {stateTag(s)}
                {s.audience === "ANONYMOUS" && (
                  <Tag color="purple">Только гостям</Tag>
                )}
                {s.activeTo && (
                  <Tag>до {dayjs(s.activeTo).format("DD.MM HH:mm")}</Tag>
                )}
              </Space>
            </Space>
          )}
        />
        <Table.Column<TvSlide>
          title="Действия"
          width={200}
          fixed="right"
          render={(_, s) => (
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                size="small"
                block
                onClick={() => push(`/tv/showcase/${s.id}`)}
              >
                Открыть
              </Button>
              <TvDelete
                resource="tv-showcase"
                id={s.id}
                name={s.title}
                entityAccusative="слайд"
                onDeleted={() => refetch()}
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
